import {NotFoundPage} from 'common';
import React from 'react';
import {Helmet} from 'react-helmet';
import {ProviderPageTitle} from './component/ProviderPageTitle';
import {Route, Switch} from 'react-router';

import {SessionStoredReferralPage} from './core/persistence/SessionStoredReferralPage';
import {NewOfferPage} from './NewOfferPage';
import {ViewEditOfferPage} from './ViewEditOfferPage';
import {ClientProfilePage} from "./client/ClientProfilePage";
import {registerPlugin} from 'react-filepond';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import {FilePondPluginFileNameMiddleEllipsis} from './client/filePondMiddleEllipsis';
import {ParentCasePage} from "./client/ParentCasePage";
import {AuthorizationListPage} from "./client/authorization/AuthorizationListPage";
import {ProviderEventUnreadCount} from "./notification/ProviderEventUnreadCount";
import {ProviderEventListPage} from "./notification/ProviderEventListPage";
import {ProviderConditionalRedirect} from "./component/ProviderConditionalRedirect";
import {ClientOfferAtRiskPage} from "./client/offer-at-risk/ClientOfferAtRiskPage";
import {ProviderMetricsPage} from "./client/provider-metrics/ProviderMetricsPage";
import {AnticipatedDischargePage} from './client/discharge/AnticipatedDischargePage';
import { AzureOauth2Environment } from 'security/component/azure/AzureOauth2Environment';
import { AzurePortalSecuredRoute } from 'security/component/azure/AzurePortalSecuredRoute';
import { useApplicationRootUrl } from 'common/component/ApplicationRootContext';
import { InternalUserProviderViewAs } from './InternalUserProviderViewAs';
import {SubmittedRequestListPage} from "./client/submitted-request/SubmittedRequestListPage";
import { RosterManagementPage } from './client/roster/RosterManagementPage';
import { ExportAuditLogPage } from './client/audit/ExportAuditLogPage';
import { ProgressPortraitDownloadAuditPage } from './client/audit/ProgressPortraitDownloadAuditPage';
import { BusinessContactPage } from './client/business-contact/BusinessContactPage';
import { FeatureFlagSwitch, ProviderPortalFeatureFlag } from 'featureflag';


registerPlugin(FilePondPluginFileValidateSize, FilePondPluginFileValidateType, FilePondPluginFileNameMiddleEllipsis);

const PROVIDER_SUPPORT_TEAM = [
    'PROVIDER_SUPPORT',
] as const;

export const AzureProviderApp = () => {
    const rootUrl = useApplicationRootUrl();

    return (
        <AzureOauth2Environment applicationType={'Client Placement'}>
        <InternalUserProviderViewAs app="client-placement">
            <Helmet>
                <meta
                    name="description"
                    content="Client Placement - The Catalight Care Services Provider Portal"
                />
            </Helmet>
            <ProviderPageTitle/>
                <ProviderEventUnreadCount>
                    <Switch>
                        <AzurePortalSecuredRoute allowedRoles={PROVIDER_SUPPORT_TEAM} path={`${rootUrl}/offer/new`}
                                           exact
                                           render={() => <NewOfferPage/>}/>
                        <AzurePortalSecuredRoute allowedRoles={PROVIDER_SUPPORT_TEAM} path={`${rootUrl}/offer/:offerGuid`}
                                           render={() => <ViewEditOfferPage/>}/>
                        <AzurePortalSecuredRoute allowedRoles={PROVIDER_SUPPORT_TEAM} path={`${rootUrl}/referral`}
                                           render={() => <SessionStoredReferralPage/>}/>
                        <AzurePortalSecuredRoute allowedRoles={PROVIDER_SUPPORT_TEAM} path={`${rootUrl}/clients/:clientGuid`}
                                           render={() => <ClientProfilePage isAzure={true}/>}/>
                        <AzurePortalSecuredRoute allowedRoles={PROVIDER_SUPPORT_TEAM} path={`${rootUrl}/clients`}
                                           render={() => <ParentCasePage/>}/>
                        <AzurePortalSecuredRoute allowedRoles={PROVIDER_SUPPORT_TEAM} path={`${rootUrl}/notifications`}
                                           render={() => <ProviderEventListPage/>}/>
                        <AzurePortalSecuredRoute allowedRoles={PROVIDER_SUPPORT_TEAM} path={`${rootUrl}/authorizations`}
                                           render={() => <AuthorizationListPage/>}/>
                        <AzurePortalSecuredRoute allowedRoles={PROVIDER_SUPPORT_TEAM} path={`${rootUrl}/clientOfferAtRisk`}
                                           render={() => <ClientOfferAtRiskPage/>}/>
                        <AzurePortalSecuredRoute allowedRoles={PROVIDER_SUPPORT_TEAM} path={`${rootUrl}/submittedRequests`}
                                                 render={() => <SubmittedRequestListPage/>}/>
                        <AzurePortalSecuredRoute allowedRoles={PROVIDER_SUPPORT_TEAM} path={`${rootUrl}/providerMetrics`}
                                           render={() => <ProviderMetricsPage/>}/>
                        <AzurePortalSecuredRoute allowedRoles={PROVIDER_SUPPORT_TEAM} path={`${rootUrl}/roster`}
                                           render={() => <RosterManagementPage/>}/>
                        {/*
                            The internal app has never used the access map for this route, so it reads
                            the flag directly. Pointing it at PortalFeatureRoute would break the page in
                            every environment: that component hardcodes the PROVIDER role and delegates
                            to PortalSecureRoute, which calls Okta's useOktaAuth, and this app runs on
                            Azure with no Okta provider above it.

                            The flag is an environment kill switch, not a permission. Where export is
                            off the page is off for everyone in that environment, Catalight staff too.
                        */}
                        <AzurePortalSecuredRoute allowedRoles={PROVIDER_SUPPORT_TEAM} path={`${rootUrl}/audit/export`}
                                           render={() => (
                                               <FeatureFlagSwitch
                                                   flag={ProviderPortalFeatureFlag.TDTL_9900_IS_EXPORT_DISABLED}
                                                   negate
                                                   fallback={<NotFoundPage/>}>
                                                   <ExportAuditLogPage/>
                                               </FeatureFlagSwitch>
                                           )}/>
                        <AzurePortalSecuredRoute allowedRoles={PROVIDER_SUPPORT_TEAM} path={`${rootUrl}/audit/progress-portrait-downloads`}
                                           render={() => (
                                               <FeatureFlagSwitch
                                                   flag={ProviderPortalFeatureFlag.BHPNPP_3219_PROGRESS_PORTRAIT_DISABLED}
                                                   negate
                                                   fallback={<NotFoundPage/>}>
                                                   <ProgressPortraitDownloadAuditPage/>
                                               </FeatureFlagSwitch>
                                           )}/>
                        {/* Reads the flag directly for the reason spelled out above the export route. */}
                        <AzurePortalSecuredRoute allowedRoles={PROVIDER_SUPPORT_TEAM} path={`${rootUrl}/contacts`}
                                           render={() => (
                                               <FeatureFlagSwitch
                                                   flag={ProviderPortalFeatureFlag.BHPNPP_3240_IS_BIZ_CONTACT_DISABLED}
                                                   negate
                                                   fallback={<NotFoundPage/>}>
                                                   <BusinessContactPage/>
                                               </FeatureFlagSwitch>
                                           )}/>
                        <AzurePortalSecuredRoute allowedRoles={PROVIDER_SUPPORT_TEAM} path={`${rootUrl}/anticipatedDischarges`}
                                           render={() => <AnticipatedDischargePage/>}/>
                        <AzurePortalSecuredRoute allowedRoles={PROVIDER_SUPPORT_TEAM} path={`${rootUrl}`}
                                           exact
                                           render={() => <ProviderConditionalRedirect/>}/>
                        <Route path='*' component={NotFoundPage}/>
                    </Switch>
                </ProviderEventUnreadCount>

        </InternalUserProviderViewAs>
        </AzureOauth2Environment>
    );
}
